import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import SpacesSection from './components/SpacesSection';
import FeaturedSection from './components/FeaturedSection';
import AboutUs from './components/AboutUs';
import CTASection from './components/CTASection';
import Catalogos from './components/Catalogos';
import ProductsList from './components/ProductsList';
import ProductDetail from './components/ProductDetail';
import QuoteCart from './components/QuoteCart';
import Contact from './components/Contact';
import Footer from './components/Footer';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import { getStoredAdminToken } from './services/adminAuthApi';

const scrollPageToTop = () => {
  const html = document.documentElement;
  const previousBehavior = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  window.scrollTo(0, 0);
  html.scrollTop = 0;
  document.body.scrollTop = 0;
  html.style.scrollBehavior = previousBehavior;
};

const isImageElement = (target) => {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(target.closest('img, picture, svg image'));
};

const QUOTE_CART_STORAGE_KEY = 'verissa-quote-cart';

const normalizeCartImages = (images) => {
  if (!Array.isArray(images)) return [];
  return images
    .map((img) => {
      if (!img) return '';
      if (typeof img === 'string') return img;
      return img.path || img.image_path || '';
    })
    .filter(Boolean);
};

const readStoredQuoteCart = () => {
  try {
    const raw = window.localStorage.getItem(QUOTE_CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && (item.id || item.productId));
  } catch {
    return [];
  }
};

function App() {
  const [currentPage, setCurrentPage] = useState('inicio');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productsNavigation, setProductsNavigation] = useState(null);
  const [quoteCartItems, setQuoteCartItems] = useState(readStoredQuoteCart);

  useEffect(() => {
    const handleHashChange = () => {
      const raw = window.location.hash.slice(1) || 'inicio';
      const page = raw.split('?')[0] || 'inicio';

      setCurrentPage(page);
      if (page !== 'productos') {
        setSelectedProduct(null);
        setProductsNavigation(null);
      }
      scrollPageToTop();
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleAddToQuoteCart = (product, quantity) => {
    const qty = Math.max(1, Number(quantity) || 1);
    if (!product?.id) return;

    setQuoteCartItems((prev) => {
      const productKey = String(product.id);
      const existingIndex = prev.findIndex(
        (item) => String(item.productId ?? item.id) === productKey
      );

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity: item.quantity + qty,
                images: product.quoteImageLocked
                  ? normalizeCartImages(product.images)
                  : item.images,
                selectedAttributes:
                  Array.isArray(product.selectedAttributes) &&
                  product.selectedAttributes.length > 0
                    ? product.selectedAttributes
                    : item.selectedAttributes,
              }
            : item
        );
      }

      return [
        ...prev,
        {
          id: product.id,
          productId: product.id,
          name: product.name,
          category: product.category,
          description: product.description,
          images: normalizeCartImages(product.images),
          selectedAttributes: product.selectedAttributes || [],
          quantity: qty,
        },
      ];
    });
  };

  const handleRemoveFromQuoteCart = (itemId) => {
    setQuoteCartItems((prev) =>
      prev.filter((item) => String(item.id) !== String(itemId))
    );
  };

  const handleClearQuoteCart = () => {
    setQuoteCartItems([]);
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(
        QUOTE_CART_STORAGE_KEY,
        JSON.stringify(quoteCartItems)
      );
    } catch {
      // El navegador puede bloquear o llenar el almacenamiento local.
    }
  }, [quoteCartItems]);

  useEffect(() => {
    const blockImageTheft = (event) => {
      if (getStoredAdminToken()) return;
      if (!isImageElement(event.target)) return;
      event.preventDefault();
    };

    document.addEventListener('contextmenu', blockImageTheft);
    document.addEventListener('dragstart', blockImageTheft);
    return () => {
      document.removeEventListener('contextmenu', blockImageTheft);
      document.removeEventListener('dragstart', blockImageTheft);
    };
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'catalogos':
        return <Catalogos />;
      case 'nosotros':
        return <AboutUs />;
      case 'contacto':
        return <Contact />;
      case 'admin-login':
        return <AdminLogin />;
      case 'admin':
        return <AdminPanel />;
      case 'carrito':
        return (
          <QuoteCart
            items={quoteCartItems}
            onRemoveItem={handleRemoveFromQuoteCart}
            onClearItems={handleClearQuoteCart}
          />
        );
      case 'productos':
        return selectedProduct ? (
          <ProductDetail
            product={selectedProduct}
            onBack={() => {
              setSelectedProduct(null);
            }}
            onAddToQuoteCart={handleAddToQuoteCart}
          />
        ) : (
          <ProductsList
            onSelectProduct={(product) => {
              setProductsNavigation(product?._navigation || null);
              setSelectedProduct(product);
              scrollPageToTop();
            }}
            initialNavigation={productsNavigation}
          />
        );
      case 'inicio':
      default:
        return (
          <>
            <Hero />
            <Features />
            <SpacesSection />
            <FeaturedSection />
            <CTASection />
          </>
        );
    }
  };

  return (
    <div className="App">
      <Navbar quoteCartCount={quoteCartItems.length} />
      {renderPage()}
      <Footer />
    </div>
  );
}

export default App;
